import * as React from "react";
import { Sources } from "../lib/models/Chat";
import {
  Tooltip,
  Card,
  CardContent,
  Grid,
  Link,
  Chip,
  Box,
  AccordionDetails,
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import LanguageIcon from "@mui/icons-material/Language";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import InfoIcon from "@mui/icons-material/Info";
import { styled } from "@mui/material/styles";

interface SourcesAccordionProps {
  sources: Sources;
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `white`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={
      <ExpandCircleDownOutlinedIcon
        transform={"rotate(-180)"}
        color={"primary"}
        sx={{ marginLeft: 0 }}
      />
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "light" ? "rgba(0, 0, 0, 0)" : "rgba(0, 0, 0, 0)",
  flexDirection: "row-reverse",
  paddingLeft: 0,
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

export default function SourcesAccordion(props: SourcesAccordionProps) {
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  return (
    <>
      <Accordion
        elevation={0}
        expanded={expanded === "sources"}
        onChange={handleChange("sources")}
      >
        <AccordionSummary
          aria-controls="sources-content"
          id="sources-header"
          color={"primary"}
        >
          <Typography
            color={"#1976D2"}
            variant="h6"
            sx={{ display: "flex", alignItems: "center" }}
          >
            Sources
            <Tooltip
              placement="right"
              title="Please note that the results are displayed from most to least semantically similar, which means they will appear from most to least relevant."
            >
              <InfoIcon sx={{ marginLeft: "2px" }} fontSize="inherit" />
            </Tooltip>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {Object.entries(props.sources).map(([key, source], index) => {
              // DEBUG: Log each source object to verify structure and type
              console.log("SourceAccordion source:", source);
              const isDocument =
                (source.type || "").toLowerCase() === "document";
              const isWeb = (source.type || "").toLowerCase() === "web";
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: "12px",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        paddingBottom: "8px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        {/* Title row with icon and type */}
                        <Box
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {isDocument && (
                            <ArticleIcon color="error" fontSize="small" />
                          )}
                          {isWeb && (
                            <LanguageIcon color="primary" fontSize="small" />
                          )}
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700 }}
                          >
                            {isDocument ? "Document" : isWeb ? "Web" : "Source"}
                          </Typography>
                          {isDocument ? (
                            <Link
                              href={source.presigned_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              variant="inherit"
                              title={source.name}
                              sx={{
                                display: "block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                width: "100%",
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                fontWeight: 500,
                                marginLeft: 1,
                              }}
                            >
                              {source.name}
                            </Link>
                          ) : isWeb ? (
                            <Link
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              variant="inherit"
                              title={source.name || source.url}
                              sx={{
                                display: "block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                width: "100%",
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                fontWeight: 500,
                                marginLeft: 1,
                              }}
                            >
                              {source.name || source.url}
                            </Link>
                          ) : null}
                        </Box>
                        {/* Page number row for PDFs only */}
                        {isDocument && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start",
                            }}
                          >
                          {typeof source.page_number !== "undefined" && (
                            <Chip
                              label={`Page ${source.page_number}`}
                              color="info"
                              size="small"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                                height: { xs: 24, sm: 28 },
                              }}
                            />
                          )}
                          </Box>
                        )}
                        {/* Content row */}
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              whiteSpace: "pre-line",
                              maxWidth: "100%",
                              overflowWrap: "break-word",
                              wordBreak: "break-word",
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                              lineHeight: 1.7,
                            }}
                          >
                            {isDocument
                              ? source.content &&
                                source.content.trim().length > 0
                                ? source.content
                                : "No content available."
                              : isWeb
                              ? source.snippet &&
                                source.snippet.trim().length > 0
                                ? source.snippet
                                : "No snippet available."
                              : "No content available."}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
}
